import localhost from "apis/localhost";
import { showErrorModal } from "slices/app/actions/error";
import {
  addToJobQueue,
  disableNavBarAndSideBar,
  removeFromJobQueue,
} from "slices/app/appSlice";

import {
  appendClarificationAnswers,
  initializeEditing,
  insertNewSectionAt,
  reset as resetOutline,
  setDocumentParagraphs,
  setGateState,
  setIsEditing,
  setOutlineData,
  setOutlineDocuments,
  setOwner,
  setRequirements,
  setSelectedIndex,
  updateOutlineSections,
} from "../outlineSlice";
import {
  selectClarificationAnswers,
  selectDocumentParagraphs,
  selectHasLoadedDocumentParagraphs,
  selectOutlineOwner,
  selectProposalOutlineSections,
} from "../selectors";
import {
  createEmptySection,
  getNextSectionIncrement,
  mergeAnswersLastWins,
  parseOutlineData,
  transformSectionsForTransport,
} from "../utils";
import { lockEditing, unlockEditing } from "./sessionActions";
import { BASE_URL, ERROR_TITLE, getOwnerUrl } from "./shared";

const STATUS_ERROR_TITLE = "There was a problem updating the section status.";

export const fetchDocumentParagraphs =
  (ownerType, ownerId, documentId) => async (dispatch, getState) => {
    if (documentId == null) {
      return [];
    }

    const state = getState();
    const isCached = selectHasLoadedDocumentParagraphs(documentId)(state);

    if (isCached) {
      return selectDocumentParagraphs(documentId)(state);
    }

    try {
      const response = await localhost.get(
        `${getOwnerUrl(ownerType, ownerId)}/documents/${documentId}/paragraphs`
      );
      const paragraphs = response?.data || [];
      dispatch(setDocumentParagraphs({ documentId, paragraphs }));
      return paragraphs;
    } catch (error) {
      console.warn(
        "Failed to load document paragraphs",
        error?.message ?? error
      );
      dispatch(setDocumentParagraphs({ documentId, paragraphs: [] }));
      return [];
    }
  };

export const fetchProposalOutlineByOwner =
  (ownerType, ownerId, muteEditingCheck = false) =>
  async (dispatch) => {
    if (!muteEditingCheck) {
      dispatch(addToJobQueue("fetchingProposal"));
    }

    try {
      const response = await localhost.get(getOwnerUrl(ownerType, ownerId));
      const isEditing = Boolean(response?.data?.lockedByCurrentUser);
      const parsedData = parseOutlineData(response?.data);

      dispatch(setOutlineData(parsedData));
      dispatch(setRequirements(parsedData?.requirements));
      dispatch(setOutlineDocuments(parsedData?.outlineDocuments));
      dispatch(setGateState(parsedData?.gateState));

      if (!muteEditingCheck) {
        dispatch(setIsEditing(isEditing));
        dispatch(disableNavBarAndSideBar(isEditing));
      }

      return response.data;
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Error fetching outline:", error);
      }
      return null;
    } finally {
      if (!muteEditingCheck) {
        dispatch(removeFromJobQueue("fetchingProposal"));
      }
    }
  };

export const resumeOutline =
  (ownerType, ownerId, answers) => async (dispatch, getState) => {
    const state = getState();
    const documentIds = state.proposalOutline.documentIds;
    const priorAnswers = selectClarificationAnswers(state);
    // Last-wins merge (not blind concat): a resubmitted prefilled answer after a failed
    // resume must replace, not duplicate, the same question.
    const allAnswers = mergeAnswersLastWins(priorAnswers, answers);
    // Accumulate BEFORE the POST so a failed resume still retains this round's answers
    // for the prefill on the next modal open (the reducer is last-wins, so re-dispatching
    // the same answers after a retry is idempotent per questionId).
    dispatch(appendClarificationAnswers(answers));
    const rsp = await localhost.post(
      `${getOwnerUrl(ownerType, ownerId)}/resume`,
      { documentIds, answers: allAnswers }
    );
    return rsp?.data?.outlineId;
  };

// Resumes a FAILED run from the stage it died in. Deliberately takes no documentIds: every
// input the resumed stage needs is already server-owned, which is the whole reason this is
// cheaper than a regenerate. Responds 202 with no outlineId, so the caller reconnects via the
// owner-scoped stream lookup.
export const retryOutline = (ownerType, ownerId) => async () => {
  const url = `${getOwnerUrl(ownerType, ownerId)}/retry`;
  const response = await localhost.post(url);
  return response.data;
};

export const deleteProposalOutlineByOwner =
  (ownerType, ownerId, { deleteDocument = false, detachParams = {} } = {}) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("deleteProposalOutline"));

    // reset() restores initialState, which also blanks the owner. The empty state that renders
    // after a delete reads the owner from the store to offer generation, and the effect that set
    // it is keyed on unchanged props so it never re-runs — put the owner back here.
    const owner = selectOutlineOwner(getState());

    try {
      const response = await localhost.delete(getOwnerUrl(ownerType, ownerId), {
        data: { deleteDocument, detachParams },
      });
      dispatch(resetOutline());

      if (owner) {
        dispatch(setOwner(owner));
      }

      return response.data;
    } catch (error) {
      dispatch(showErrorModal(error, ERROR_TITLE));
      return null;
    } finally {
      dispatch(removeFromJobQueue("deleteProposalOutline"));
    }
  };

export const initializeManualEditing =
  (ownerType, ownerId) => async (dispatch) => {
    const canEdit = await dispatch(lockEditing(ownerType, ownerId));

    if (!canEdit) {
      return false;
    }

    dispatch(addToJobQueue("createManualOutline"));
    const sections = [createEmptySection()];
    try {
      const url = `${BASE_URL}/manual`;
      await localhost.post(url, {
        ownerType,
        ownerId,
        outlineData: {
          alignmentSummary: "Manual alignment",
          sections: transformSectionsForTransport(sections),
        },
      });

      dispatch(fetchProposalOutlineByOwner(ownerType, ownerId));
    } catch (error) {
      dispatch(showErrorModal(error, ERROR_TITLE));
      // Entering the editor here would hand the user a working-looking table over an outline that
      // was never created, holding a lock on nothing until it expires; their first save would go
      // to an outline the server does not have.
      dispatch(unlockEditing(ownerType, ownerId));
      return false;
    } finally {
      dispatch(removeFromJobQueue("createManualOutline"));
    }

    dispatch(initializeEditing());
    return true;
  };

export const saveProposalOutline =
  (ownerType, ownerId, documentSync = null) =>
  async (dispatch, getState) => {
    const state = getState();
    const sections = selectProposalOutlineSections(state);

    dispatch(addToJobQueue("updateProposalOutline"));

    try {
      const url = getOwnerUrl(ownerType, ownerId);
      const payload = {
        outlineData: {
          sections: transformSectionsForTransport(sections),
        },
      };

      if (documentSync) {
        payload.documentSync = documentSync;
      }

      const response = await localhost.put(url, payload);

      dispatch(setOutlineData(parseOutlineData(response.data)));
      dispatch(setIsEditing(false));
      dispatch(disableNavBarAndSideBar(false));
      dispatch(setSelectedIndex());

      await dispatch(unlockEditing(ownerType, ownerId));

      return response.data;
    } catch (error) {
      dispatch(showErrorModal(error, ERROR_TITLE));
      return null;
    } finally {
      dispatch(removeFromJobQueue("updateProposalOutline"));
    }
  };

export const updateProposalOutline = (ownerType, ownerId) =>
  saveProposalOutline(ownerType, ownerId);

export const insertNewSection =
  ({ index, level }) =>
  (dispatch, getState) => {
    const state = getState();
    const sections = selectProposalOutlineSections(state);

    const increment = getNextSectionIncrement(sections);
    const newSection = createEmptySection(increment, level);

    dispatch(insertNewSectionAt({ index, newSection }));

    return newSection;
  };

export const updateSectionStatus =
  (ownerType, ownerId, sectionId, status, subsectionIds = []) =>
  async (dispatch, getState) => {
    const previousSections = selectProposalOutlineSections(getState());
    const affectedIds = new Set([sectionId, ...subsectionIds]);
    const optimisticSections = previousSections.map((section) =>
      affectedIds.has(section.id) ? { ...section, status } : section
    );
    dispatch(updateOutlineSections(optimisticSections));

    try {
      const url = `${getOwnerUrl(ownerType, ownerId)}/items/${sectionId}/status`;
      await localhost.patch(url, { status, cascade: subsectionIds.length > 0 });
      return status;
    } catch (error) {
      dispatch(updateOutlineSections(previousSections));
      dispatch(showErrorModal(error, STATUS_ERROR_TITLE));
      return null;
    }
  };
